import React, { useEffect, useState } from "react";
import { exportSelectedSheetsToPdf, listWorkbookSheets } from "../excel/exportBoqPdf";

export default function ExportPdfPage({ onBack, projects = [], customers = [] }) {
  const [withCover, setWithCover] = useState(true);
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [summary, setSummary] = useState(null);
  const [allSheets, setAllSheets] = useState([]);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState("");
  const [progress, setProgress] = useState("");

  const loadSheets = () => {
    setStatus("loading");
    setMessage("");
    setSummary(null);
    listWorkbookSheets()
      .then(({ activeName, allNames, recommendedNames }) => {
        setActiveSheet(activeName || "");
        setAllSheets(allNames || []);
        setSelectedSheets(recommendedNames && recommendedNames.length ? recommendedNames : allNames || []);
        setStatus("ready");
      })
      .catch((err) => {
        setMessage(err?.message || "ფურცლების ჩატვირთვა ვერ მოხერხდა");
        setStatus("error");
      });
  };

  const runExport = () => {
    if (!selectedSheets.length) {
      setStatus("error");
      setMessage("აირჩიე მინიმუმ ერთი ფურცელი.");
      return;
    }
    setStatus("running");
    setMessage("");
    setSummary(null);
    setProgress("");
    let cover = null;
    if (withCover) {
      let activeId = "";
      try { activeId = window.localStorage.getItem("boq.activeProjectId") || ""; } catch { /* ignore */ }
      const project = projects.find((p) => String(p.id) === String(activeId)) || null;
      const customer = project ? customers.find((c) => String(c.id) === String(project.customer_id)) : null;
      cover = {
        projectName: project?.name || "",
        customer: customer ? (customer.inn ? `${customer.name} / ${customer.inn}` : customer.name) : "",
        location: project?.location || "",
        area: project?.area || "",
        docNumber: project?.doc_number || "",
        date: project?.date || "",
        author: "",
      };
    }
    exportSelectedSheetsToPdf(selectedSheets, setProgress, { cover })
      .then((result) => { setSummary(result); setStatus("done"); })
      .catch((err) => { setMessage(err?.message || "უცნობი შეცდომა"); setStatus("error"); });
  };

  useEffect(() => { loadSheets(); }, []);

  const toggleSheet = (name) =>
    setSelectedSheets((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );

  const btnBase = {
    borderRadius: 6, padding: "8px 12px", fontSize: 12, cursor: "pointer",
    border: "1px solid rgba(148,163,184,0.25)",
    background: "var(--surface-soft)", color: "var(--text-muted)", flex: 1,
  };

  return (
    <div style={{
      minHeight: "100vh", background: "var(--page-gradient)",
      padding: "20px 16px", color: "var(--text-primary)",
      fontFamily: "Arial, sans-serif",
    }}>
      <button className="link" onClick={onBack}>← უკან</button>

      <h2 style={{ marginTop: 16, marginBottom: 4 }}>PDF ექსპორტი</h2>
      <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.5 }}>
        შეარჩიე BOQ ფურცლები და ჩამოტვირთე PDF ფორმატში.
      </p>

      {/* Loading */}
      {status === "loading" && (
        <div style={{
          background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
          borderRadius: 10, padding: "32px 0", textAlign: "center",
          fontSize: 13, color: "var(--text-muted)",
        }}>
          BOQ ფურცლების კითხვა...
        </div>
      )}

      {/* Running */}
      {status === "running" && (
        <div style={{
          background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
          borderRadius: 10, padding: "32px 0", textAlign: "center",
          fontSize: 13, color: "var(--text-muted)",
        }}>
          PDF გენერაცია მიმდინარეობს...
          {progress && <div style={{ marginTop: 6, fontSize: 11 }}>{progress}</div>}
        </div>
      )}

      {/* Ready */}
      {status === "ready" && (
        <div>
          <div style={{
            background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
            borderRadius: 8, padding: "10px 12px", marginBottom: 10,
            fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5,
          }}>
            აქტიური ფურცელი: <strong style={{ color: "var(--text-primary)" }}>{activeSheet || "—"}</strong>
            <br />
            მონიშნულია აქტიური და child ფურცლები. შეგიძლია შეცვალო მონიშვნა.
          </div>

          <div style={{
            border: "1px solid rgba(148,163,184,0.2)",
            borderRadius: 8, maxHeight: 260, overflowY: "auto", marginBottom: 10,
            background: "var(--surface-soft)",
          }}>
            {allSheets.map((name) => {
              const checked = selectedSheets.includes(name);
              return (
                <label
                  key={name}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 12px",
                    borderBottom: "1px solid rgba(148,163,184,0.08)",
                    cursor: "pointer", fontSize: 12,
                    background: checked ? "rgba(6,182,212,0.06)" : "transparent",
                    color: checked ? "var(--text-primary)" : "var(--text-muted)",
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleSheet(name)} />
                  <span style={{ flex: 1 }}>{name}</span>
                </label>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button onClick={() => setSelectedSheets(allSheets)} style={btnBase}>ყველა</button>
            <button onClick={() => setSelectedSheets([])} style={btnBase}>მოხსნა</button>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={withCover} onChange={(e) => setWithCover(e.target.checked)} />
            <span>სატიტულო გვერდი დამკვეთისთვის (პროექტი, ჯამები, 1 მ² ღირებულება)</span>
          </label>

          <button
            onClick={runExport}
            style={{
              width: "100%", padding: "10px", borderRadius: 8,
              border: "none", background: "var(--accent-primary)", color: "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            PDF ექსპორტი ({selectedSheets.length})
          </button>
        </div>
      )}

      {/* Done */}
      {status === "done" && (
        <div>
          <div style={{
            background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.3)",
            borderRadius: 10, padding: "14px 16px", marginBottom: 12,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>✓ PDF წარმატებით გენერირდა</div>
            {summary && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.7 }}>
                <div>ფაილი: <strong style={{ color: "var(--text-primary)" }}>{summary.fileName || "—"}</strong></div>
                <div>ფურცლები: {summary.selectedCount} · გვერდები: {summary.pages}</div>
              </div>
            )}
          </div>
          <div style={{
            background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
            borderRadius: 8, padding: "10px 12px", marginBottom: 12,
            fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5,
          }}>
            PDF ფაილი ავტომატურად ჩამოიტვირთა ბრაუზერიდან.
          </div>
          <button
            onClick={loadSheets}
            style={{
              width: "100%", padding: "9px", borderRadius: 8,
              border: "none", background: "var(--accent-primary)", color: "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            ახლიდან არჩევა
          </button>
        </div>
      )}

      {/* Error */}
      {status === "error" && (
        <div>
          <div style={{
            background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 10, padding: "12px 14px", marginBottom: 14, color: "#fca5a5",
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>შეცდომა</div>
            <div style={{ fontSize: 12 }}>{message}</div>
          </div>
          <button
            onClick={loadSheets}
            style={{
              width: "100%", padding: "9px", borderRadius: 8,
              border: "none", background: "var(--accent-primary)", color: "#fff",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
            }}
          >
            კვლავ სცადე
          </button>
        </div>
      )}
    </div>
  );
}
